import { type yup } from '@framework/validation/initYup.ts';
import { type Dictionary } from 'lodash';
import { isParentWidgetScopeReadonly } from '../../../../validator/utils/parent-scope/isParentWidgetScopeReadonly.ts';
import { mapReferenceToPath } from '../../../expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '../../../expressions/evaluate/variables/IEvaluationVariables.ts';
import { type IExpression } from '../../../expressions/IExpression.ts';
import { collectExpressionReferences } from '../../../expressions/references/collectExpressionReferences.ts';
import { type IParentWidgetScope } from '../../IWidgetType.ts';

/**
 * Aggiunge il transform per i widget calcolati stringa/numero.
 * La evaluateFn è la funzione per calcolare il risultato dell'espressione
 * che verrà poi applicato al campo.
 */
export function setComputeTransform<T extends IExpression>(
  expression: T,
  parentScope: IParentWidgetScope,
  schema: yup.AnySchema,
  evaluateFn: (expression: T, variables: Dictionary<any>) => any,
) {
  // In Readonly, non applichiamo la trasformazione in quanto comunque
  // il campo non è modificabile e il valore corretto è quello consolidato.
  if (isParentWidgetScopeReadonly(parentScope)) return schema;

  const references = collectExpressionReferences(expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return schema.parentWhen(
    paths,
    (values, schema: yup.AnySchema, context: any) => {
      const variables = createEvaluationVariables(references, values);

      const result = evaluateFn(expression, variables);

      // Yup salta i transform su `undefined`: se il campo è assente (es. riga
      // appena materializzata dal default di un repeatable), il transform non
      // gira e il valore resta al default. Usando il valore calcolato come
      // default, il primo cast converge immediatamente senza bisogno di un
      // secondo passaggio. Il transform copre gli aggiornamenti successivi
      // (quando il valore è già presente e le dipendenze cambiano).
      return schema.default(() => result).transform(() => result);
    },
  );
}
